<template>
  <div class="season-phase-settings">
    <!-- Header with Current Phase Badge -->
    <div class="settings-header">
      <h3>Season Phase Configuration</h3>
      <div
        class="current-phase-badge"
        :style="{
          backgroundColor: currentPhaseInfo.bgColor,
          borderColor: currentPhaseInfo.borderColor,
        }"
      >
        <span class="phase-icon">{{ currentPhaseInfo.emoji }}</span>
        <span
          class="phase-label"
          :style="{ color: currentPhaseInfo.color }"
        >
          {{ currentPhaseInfo.label }}
        </span>
        <span v-if="phaseData.is_override" class="override-badge">
          Override Active
        </span>
      </div>
    </div>

    <p class="section-description">
      Set the club's season calendar. All teams inherit these dates by default
      and use them for filtering games, injuries, wellness, performance, and
      nutrition data. Teams with their own dates set will continue to use those
      until cleared in the Team Overrides section below.
    </p>

    <!-- Next Season Rollover Status -->
    <div
      v-if="isAdmin && nextSeasonStatus.next_year"
      class="next-season-block"
    >
      <div class="next-season-row">
        <div>
          <span class="next-season-label">Next season:</span>
          <strong>{{ nextSeasonStatus.next_year }}</strong>
          <span
            v-if="nextSeasonStatus.next_configured"
            class="next-season-status configured"
          >Configured</span>
          <span v-else class="next-season-status pending">Not yet configured</span>
        </div>
        <button
          v-if="!nextSeasonStatus.next_configured"
          class="btn btn-secondary btn-sm"
          :disabled="rollingForward"
          @click="rollForward"
        >
          {{ rollingForward ? "Rolling forward…" : "Roll forward now" }}
        </button>
      </div>
      <p class="help-text next-season-help">
        The system will roll forward automatically when this season ends. You
        can also do it manually here to plan ahead — dates will be copied with
        one year added.
      </p>
    </div>

    <!-- Season Timeline Visualization -->
    <div class="season-timeline-section">
      <h4>Season Timeline {{ config.season_year }}</h4>
      <div class="timeline-container">
        <div class="timeline-bar">
          <div
            v-for="phase in timelinePhases"
            :key="phase.id"
            class="timeline-segment"
            :class="{ active: phase.id === phaseData.phase }"
            :style="{
              width: phase.width + '%',
              backgroundColor: phase.color,
            }"
            :title="phase.label + ': ' + phase.dateRange"
          >
            <span class="segment-label">{{ phase.shortLabel }}</span>
          </div>
        </div>
        <div
          class="timeline-marker"
          :style="{ left: todayPosition + '%' }"
        >
          <div class="marker-line"></div>
          <span class="marker-label">Today</span>
        </div>
      </div>
    </div>

    <!-- Use Defaults Toggle -->
    <div class="form-section">
      <div class="toggle-row">
        <label class="toggle-label">
          <input
            type="checkbox"
            v-model="config.use_defaults"
            @change="handleDefaultsToggle"
          />
          <span class="toggle-text">Use Default Dates</span>
        </label>
        <p class="help-text">{{ defaultsHelpText }}</p>
      </div>
    </div>

    <!-- Custom Date Configuration -->
    <div v-if="!config.use_defaults" class="phase-dates-config">
      <h4>Custom Phase Dates</h4>

      <div
        class="phase-row"
        v-for="phase in editablePhases"
        :key="phase.id"
      >
        <div class="phase-info">
          <span
            class="phase-color-dot"
            :style="{ backgroundColor: phase.color }"
          ></span>
          <span class="phase-name">{{ phase.label }}</span>
        </div>
        <div class="phase-dates">
          <div class="date-input">
            <label>Start</label>
            <VueDatePicker
              v-model="config[phase.startKey]"
              :enable-time-picker="false"
              format="dd/MM/yyyy"
              :locale="'en-GB'"
              placeholder="Select start date"
              auto-apply
              @update:model-value="onDateChange(phase.startKey, $event)"
            />
          </div>
          <div class="date-input">
            <label>End</label>
            <VueDatePicker
              v-model="config[phase.endKey]"
              :enable-time-picker="false"
              format="dd/MM/yyyy"
              :locale="'en-GB'"
              placeholder="Select end date"
              auto-apply
              @update:model-value="onDateChange(phase.endKey, $event)"
            />
          </div>
        </div>
      </div>

      <!-- Playoffs Toggle -->
      <div class="toggle-row playoffs-toggle">
        <label class="toggle-label">
          <input
            type="checkbox"
            v-model="config.has_playoffs"
          />
          <span class="toggle-text">Enable Playoffs/Post-season Phase</span>
        </label>
        <p class="help-text">
          Enable this if your league has a playoff or knockout phase after the
          regular season.
        </p>
      </div>

      <!-- Playoff Dates (if enabled) -->
      <div
        v-if="config.has_playoffs"
        class="phase-row playoff-dates"
      >
        <div class="phase-info">
          <span
            class="phase-color-dot"
            :style="{ backgroundColor: '#fd7e14' }"
          ></span>
          <span class="phase-name">Playoffs</span>
        </div>
        <div class="phase-dates">
          <div class="date-input">
            <label>Start</label>
            <VueDatePicker
              v-model="config.postseason_start"
              :enable-time-picker="false"
              format="dd/MM/yyyy"
              :locale="'en-GB'"
              placeholder="Playoff start"
              auto-apply
            />
          </div>
          <div class="date-input">
            <label>End</label>
            <VueDatePicker
              v-model="config.postseason_end"
              :enable-time-picker="false"
              format="dd/MM/yyyy"
              :locale="'en-GB'"
              placeholder="Playoff end"
              auto-apply
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Team Overrides Section -->
    <div
      v-if="isAdmin"
      class="team-overrides-section"
    >
      <h4>Team Season Date Overrides</h4>
      <p class="help-text section-help-text">
        Teams below have their own season dates set and will <strong>not</strong>
        inherit the club calendar above. Clear an override to have that team
        use the club default.
      </p>

      <div v-if="loadingOverrides" class="overrides-loading">
        Loading…
      </div>

      <div
        v-else-if="overridingTeams.length === 0"
        class="overrides-empty"
      >
        <span class="check-icon">✓</span>
        All teams are inheriting the club calendar.
      </div>

      <div v-else class="overrides-list">
        <div
          v-for="team in overridingTeams"
          :key="team.id"
          class="override-row"
        >
          <div class="override-team-info">
            <span class="override-team-name">{{ team.teamName }}</span>
            <span v-if="team.age_group" class="override-team-age">{{ team.age_group }}</span>
          </div>
          <div class="override-team-dates">
            {{ formatDate(team.season_start_date) || "—" }}
            →
            {{ formatDate(team.season_end_date) || "—" }}
          </div>
          <button
            class="btn btn-link"
            :disabled="clearingTeamId === team.id"
            @click="clearTeamOverride(team)"
          >
            {{ clearingTeamId === team.id ? "Clearing…" : "Use club default" }}
          </button>
        </div>

        <div class="overrides-bulk">
          <button
            class="btn btn-secondary"
            :disabled="clearingAll"
            @click="clearAllOverrides"
          >
            {{ clearingAll ? "Resetting…" : `Reset all ${overridingTeams.length} teams to club default` }}
          </button>
        </div>
      </div>
    </div>

    <!-- Admin Override Section -->
    <div
      v-if="isAdmin"
      class="override-section"
    >
      <h4>Admin Phase Override</h4>
      <p class="help-text">
        Temporarily override the calculated season phase. Useful for special
        circumstances like mid-season breaks or tournament preparation.
      </p>

      <div class="override-controls">
        <div class="form-group">
          <label>Override to Phase</label>
          <select
            v-model="overridePhase"
            class="form-select"
          >
            <option value="">-- No Override (Use Calculated) --</option>
            <option value="preseason">Pre-Season</option>
            <option value="regular">Regular Season</option>
            <option value="postseason">Playoffs</option>
            <option value="offseason">Off-Season</option>
          </select>
        </div>

        <div
          class="form-group"
          v-if="overridePhase"
        >
          <label>Override Expires (Optional)</label>
          <VueDatePicker
            v-model="overrideExpires"
            :enable-time-picker="false"
            format="dd/MM/yyyy"
            :locale="'en-GB'"
            placeholder="Leave empty for indefinite"
            auto-apply
          />
        </div>

        <div
          class="form-group"
          v-if="overridePhase"
        >
          <label>Reason (Optional)</label>
          <input
            type="text"
            v-model="overrideReason"
            class="form-input"
            placeholder="e.g., Tournament preparation"
          />
        </div>
      </div>

      <div class="override-buttons">
        <button
          v-if="overridePhase"
          class="btn btn-warning"
          @click="applyOverride"
          :disabled="saving"
        >
          Apply Override
        </button>
        <button
          v-if="phaseData.is_override"
          class="btn btn-secondary"
          @click="clearOverride"
          :disabled="saving"
        >
          Clear Override
        </button>
      </div>

      <div
        v-if="phaseData.is_override"
        class="current-override-info"
      >
        <p>
          <strong>Current Override:</strong>
          {{ getPhaseDisplayInfo(phaseData.phase).label }}
        </p>
        <p v-if="phaseData.expires">
          <strong>Expires:</strong> {{ formatDate(phaseData.expires) }}
        </p>
        <p v-if="phaseData.reason">
          <strong>Reason:</strong> {{ phaseData.reason }}
        </p>
      </div>
    </div>

    <!-- Save Button -->
    <div class="settings-actions">
      <button
        class="green-button"
        @click="saveConfig"
        :disabled="saving"
      >
        {{ saving ? "Saving..." : "Save Configuration" }}
      </button>
      <span
        v-if="saveSuccess"
        class="save-success"
      >
        Configuration saved successfully!
      </span>
    </div>
  </div>
</template>

<script>
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { SeasonPhases } from "@/services/season-phases-service";
import { Teams } from "@/services/teams-service";
import { mapGetters } from "vuex";

export default {
  name: "SeasonPhaseSettings",
  components: { VueDatePicker },

  data() {
    return {
      config: {
        use_defaults: true,
        has_playoffs: false,
        preseason_start: null,
        preseason_end: null,
        regular_season_start: null,
        regular_season_end: null,
        postseason_start: null,
        postseason_end: null,
        offseason_start: null,
        offseason_end: null,
        season_year: "",
      },
      phaseData: {
        phase: "regular",
        source: "calculated",
        is_override: false,
        expires: null,
        reason: null,
      },
      overridePhase: "",
      overrideExpires: null,
      overrideReason: "",
      saving: false,
      saveSuccess: false,
      overridingTeams: [],
      loadingOverrides: false,
      clearingTeamId: null,
      clearingAll: false,
      nextSeasonStatus: {
        current_year: "",
        next_year: "",
        next_configured: false,
      },
      rollingForward: false,
      editablePhases: [
        {
          id: "preseason",
          label: "Pre-Season",
          shortLabel: "Pre",
          color: "#28a745",
          startKey: "preseason_start",
          endKey: "preseason_end",
        },
        {
          id: "regular",
          label: "Regular Season",
          shortLabel: "Season",
          color: "#007bff",
          startKey: "regular_season_start",
          endKey: "regular_season_end",
        },
        {
          id: "offseason",
          label: "Off-Season",
          shortLabel: "Off",
          color: "#6c757d",
          startKey: "offseason_start",
          endKey: "offseason_end",
        },
      ],
    };
  },

  computed: {
    ...mapGetters(["getProfile"]),

    isAdmin() {
      return this.getProfile?.userType === "Admin";
    },

    clubId() {
      return this.getProfile?.clubId;
    },

    currentPhaseInfo() {
      return SeasonPhases.getPhaseDisplayInfo(this.phaseData.phase);
    },

    timelinePhases() {
      // Widths are derived from the actual configured dates so the bar reflects
      // the club's real season — calendar seasons (e.g. AU Mar–Sep) render
      // correctly instead of using the old hardcoded UK proportions.
      const days = (a, b) => {
        const ta = a ? new Date(a).getTime() : null;
        const tb = b ? new Date(b).getTime() : null;
        return ta && tb ? Math.max(0, (tb - ta) / 86400000) : 0;
      };
      const seasonStart =
        this.config.preseason_start || this.config.regular_season_start;
      const seasonEnd =
        this.config.offseason_end || this.config.regular_season_end;
      const total = days(seasonStart, seasonEnd) || 365;
      const pct = (a, b) => Math.round((days(a, b) / total) * 100);

      return [
        {
          id: "preseason",
          label: "Pre-Season",
          shortLabel: "Pre",
          color: "#28a745",
          width: pct(this.config.preseason_start, this.config.preseason_end),
          dateRange: this.formatDateRange(
            this.config.preseason_start,
            this.config.preseason_end
          ),
        },
        {
          id: "regular",
          label: "Regular Season",
          shortLabel: "Season",
          color: "#007bff",
          width: pct(
            this.config.regular_season_start,
            this.config.regular_season_end
          ),
          dateRange: this.formatDateRange(
            this.config.regular_season_start,
            this.config.regular_season_end
          ),
        },
        {
          id: "offseason",
          label: "Off-Season",
          shortLabel: "Off",
          color: "#6c757d",
          width: pct(this.config.offseason_start, this.config.offseason_end),
          dateRange: this.formatDateRange(
            this.config.offseason_start,
            this.config.offseason_end
          ),
        },
      ];
    },

    todayPosition() {
      // Today's position across the ACTUAL season window (preseason start →
      // offseason end), so it works for any season style, not just a UK
      // June-start season.
      const t = (d) => (d ? new Date(d).getTime() : null);
      const seasonStart = t(
        this.config.preseason_start || this.config.regular_season_start
      );
      const seasonEnd = t(
        this.config.offseason_end || this.config.regular_season_end
      );
      if (!seasonStart || !seasonEnd || seasonEnd <= seasonStart) return 0;
      const pos = ((Date.now() - seasonStart) / (seasonEnd - seasonStart)) * 100;
      return Math.min(100, Math.max(0, Math.round(pos)));
    },

    // Help text derived from the loaded config so it names the club's real
    // months (UK "Aug-May", AU "Mar-Sep", …) instead of hardcoded UK.
    defaultsHelpText() {
      const mon = (d) =>
        d ? new Date(d).toLocaleString("en-GB", { month: "short" }) : null;
      const preS = mon(this.config.preseason_start);
      const preE = mon(this.config.preseason_end);
      const regS = mon(this.config.regular_season_start);
      const regE = mon(this.config.regular_season_end);
      if (!regS || !regE) {
        return "Defaults follow your club's season configuration.";
      }
      const pre = preS
        ? `Pre-season (${preE && preE !== preS ? preS + "-" + preE : preS}), `
        : "";
      return `Defaults: ${pre}Regular Season (${regS}-${regE}), Off-season (2 weeks post-season)`;
    },
  },

  async mounted() {
    await this.loadConfig();
  },

  methods: {
    getPhaseDisplayInfo(phase) {
      return SeasonPhases.getPhaseDisplayInfo(phase);
    },

    formatDate(date) {
      return SeasonPhases.formatDateForDisplay(date);
    },

    formatDateRange(start, end) {
      if (!start || !end) return "Not configured";
      return `${this.formatDate(start)} - ${this.formatDate(end)}`;
    },

    async loadConfig() {
      if (!this.clubId) return;

      this.loadOverridingTeams();
      this.loadNextSeasonStatus();

      try {
        const [phaseResponse, configResponse] = await Promise.all([
          SeasonPhases.getCurrentPhase(this.clubId),
          SeasonPhases.getPhaseConfig(this.clubId),
        ]);

        if (phaseResponse.data.success) {
          this.phaseData = { ...this.phaseData, ...phaseResponse.data.data };
        }

        if (configResponse.data.success) {
          const configData = configResponse.data.data;
          // Convert date strings to Date objects for the date picker
          this.config = {
            ...this.config,
            ...configData,
            preseason_start: configData.preseason_start
              ? new Date(configData.preseason_start)
              : null,
            preseason_end: configData.preseason_end
              ? new Date(configData.preseason_end)
              : null,
            regular_season_start: configData.regular_season_start
              ? new Date(configData.regular_season_start)
              : null,
            regular_season_end: configData.regular_season_end
              ? new Date(configData.regular_season_end)
              : null,
            postseason_start: configData.postseason_start
              ? new Date(configData.postseason_start)
              : null,
            postseason_end: configData.postseason_end
              ? new Date(configData.postseason_end)
              : null,
            offseason_start: configData.offseason_start
              ? new Date(configData.offseason_start)
              : null,
            offseason_end: configData.offseason_end
              ? new Date(configData.offseason_end)
              : null,
          };
        }

      } catch (error) {
        console.error("Failed to load season phase config:", error);
      }
    },

    handleDefaultsToggle() {
      if (this.config.use_defaults) {
        // Reset custom dates when switching to defaults
        this.config.preseason_start = null;
        this.config.preseason_end = null;
        this.config.regular_season_start = null;
        this.config.regular_season_end = null;
        this.config.postseason_start = null;
        this.config.postseason_end = null;
        this.config.offseason_start = null;
        this.config.offseason_end = null;
      }
    },

    onDateChange(key, value) {
      this.config[key] = value;
    },

    async saveConfig() {
      this.saving = true;
      this.saveSuccess = false;

      try {
        // Format dates for database
        const configToSave = {
          use_defaults: this.config.use_defaults ? 1 : 0,
          has_playoffs: this.config.has_playoffs ? 1 : 0,
          preseason_start: SeasonPhases.formatDateForDb(
            this.config.preseason_start
          ),
          preseason_end: SeasonPhases.formatDateForDb(
            this.config.preseason_end
          ),
          regular_season_start: SeasonPhases.formatDateForDb(
            this.config.regular_season_start
          ),
          regular_season_end: SeasonPhases.formatDateForDb(
            this.config.regular_season_end
          ),
          postseason_start: SeasonPhases.formatDateForDb(
            this.config.postseason_start
          ),
          postseason_end: SeasonPhases.formatDateForDb(
            this.config.postseason_end
          ),
          offseason_start: SeasonPhases.formatDateForDb(
            this.config.offseason_start
          ),
          offseason_end: SeasonPhases.formatDateForDb(
            this.config.offseason_end
          ),
        };

        const response = await SeasonPhases.savePhaseConfig(
          this.clubId,
          configToSave
        );

        if (response.data.success) {
          this.saveSuccess = true;
          this.$emit("saved");
          // Reload to get updated calculated phase
          await this.loadConfig();
          // Clear success message after 3 seconds
          setTimeout(() => {
            this.saveSuccess = false;
          }, 3000);
        }
      } catch (error) {
        console.error("Failed to save config:", error);
      } finally {
        this.saving = false;
      }
    },

    async applyOverride() {
      if (!this.overridePhase) return;

      this.saving = true;
      try {
        const expires = this.overrideExpires
          ? SeasonPhases.formatDateForDb(this.overrideExpires)
          : null;

        const response = await SeasonPhases.setPhaseOverride(
          this.clubId,
          this.overridePhase,
          expires,
          this.overrideReason
        );

        if (response.data.success) {
          await this.loadConfig();
          this.overridePhase = "";
          this.overrideExpires = null;
          this.overrideReason = "";
        }
      } catch (error) {
        console.error("Failed to apply override:", error);
      } finally {
        this.saving = false;
      }
    },

    async clearOverride() {
      this.saving = true;
      try {
        const response = await SeasonPhases.clearPhaseOverride(this.clubId);
        if (response.data.success) {
          await this.loadConfig();
        }
      } catch (error) {
        console.error("Failed to clear override:", error);
      } finally {
        this.saving = false;
      }
    },

    async loadNextSeasonStatus() {
      if (!this.clubId) return;
      try {
        const response = await SeasonPhases.getNextSeasonStatus(this.clubId);
        if (response.data && response.data.success) {
          this.nextSeasonStatus = response.data.data;
        }
      } catch (error) {
        console.error("Failed to load next season status:", error);
      }
    },

    async rollForward() {
      if (!this.clubId) return;
      this.rollingForward = true;
      try {
        const response = await SeasonPhases.rollForwardSeason(this.clubId);
        if (response.data && response.data.success) {
          await this.loadNextSeasonStatus();
        } else if (response.data?.data?.reason === "already_exists") {
          await this.loadNextSeasonStatus();
        }
      } catch (error) {
        console.error("Failed to roll forward season:", error);
      } finally {
        this.rollingForward = false;
      }
    },

    async loadOverridingTeams() {
      if (!this.clubId) return;
      this.loadingOverrides = true;
      try {
        const response = await Teams.getTeamsWithSeasonOverrides(this.clubId);
        this.overridingTeams = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Failed to load overriding teams:", error);
        this.overridingTeams = [];
      } finally {
        this.loadingOverrides = false;
      }
    },

    async clearTeamOverride(team) {
      const confirmed = window.confirm(
        `Clear season dates for "${team.teamName}"? It will inherit the club calendar.`
      );
      if (!confirmed) return;

      this.clearingTeamId = team.id;
      try {
        const response = await Teams.clearTeamSeasonOverride(
          team.id,
          this.getProfile?.id
        );
        if (response.data && response.data.success) {
          await this.loadOverridingTeams();
        }
      } catch (error) {
        console.error("Failed to clear team override:", error);
      } finally {
        this.clearingTeamId = null;
      }
    },

    async clearAllOverrides() {
      const count = this.overridingTeams.length;
      const confirmed = window.confirm(
        `Reset all ${count} team${count === 1 ? "" : "s"} to the club default? This cannot be undone — each team would need its dates re-entered manually.`
      );
      if (!confirmed) return;

      this.clearingAll = true;
      try {
        const response = await Teams.clearAllTeamSeasonOverrides(
          this.clubId,
          this.getProfile?.id
        );
        if (response.data && response.data.success) {
          await this.loadOverridingTeams();
        }
      } catch (error) {
        console.error("Failed to clear all overrides:", error);
      } finally {
        this.clearingAll = false;
      }
    },
  },
};
</script>

<style scoped>
.season-phase-settings {
  padding: 0;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.settings-header h3 {
  margin: 0;
  font-size: 1.25rem;
}

.section-description {
  color: #6c757d;
  margin-bottom: 24px;
  font-size: 0.9rem;
}

.current-phase-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid;
}

.phase-icon {
  font-size: 1.1em;
}

.phase-label {
  font-weight: 600;
  font-size: 0.9rem;
}

.override-badge {
  background: #dc3545;
  color: white;
  font-size: 0.7em;
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 4px;
}

/* Timeline Section */
.season-timeline-section {
  margin-bottom: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.season-timeline-section h4 {
  margin: 0 0 16px 0;
  font-size: 1rem;
}

.timeline-container {
  position: relative;
  padding-bottom: 24px;
}

.timeline-bar {
  display: flex;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
}

.timeline-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75em;
  font-weight: 500;
  transition: opacity 0.2s;
  opacity: 0.7;
}

.timeline-segment.active {
  opacity: 1;
}

.timeline-segment:hover {
  opacity: 1;
}

.segment-label {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.timeline-marker {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
}

.marker-line {
  width: 2px;
  height: 40px;
  background: #dc3545;
}

.marker-label {
  display: block;
  font-size: 0.7em;
  color: #dc3545;
  white-space: nowrap;
  margin-top: 4px;
  font-weight: 600;
}

/* Form Sections */
.form-section {
  margin-bottom: 24px;
}

.toggle-row {
  margin-bottom: 16px;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.toggle-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.toggle-text {
  font-weight: 500;
}

.help-text {
  color: #6c757d;
  font-size: 0.85rem;
  margin: 4px 0 0 26px;
}

/* Phase Dates Config */
.phase-dates-config {
  margin-bottom: 24px;
  padding: 16px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}

.phase-dates-config h4 {
  margin: 0 0 16px 0;
  font-size: 1rem;
}

.phase-row {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  gap: 16px;
  flex-wrap: wrap;
}

.phase-row:last-child {
  border-bottom: none;
}

.phase-info {
  width: 140px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.phase-color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.phase-name {
  font-weight: 500;
  font-size: 0.9rem;
}

.phase-dates {
  display: flex;
  gap: 16px;
  flex: 1;
  min-width: 300px;
}

.date-input {
  flex: 1;
  min-width: 140px;
}

.date-input label {
  display: block;
  font-size: 0.75em;
  color: #666;
  margin-bottom: 4px;
}

.playoffs-toggle {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.playoff-dates {
  background: #fff8f0;
  padding: 12px;
  border-radius: 4px;
  margin-top: 8px;
}

/* Override Section */
.override-section {
  margin-bottom: 24px;
  padding: 16px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 8px;
}

.override-section h4 {
  margin: 0 0 8px 0;
  font-size: 1rem;
  color: #856404;
}

.override-controls {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin: 16px 0;
}

.form-group {
  flex: 1;
  min-width: 200px;
}

.form-group label {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: 4px;
  color: #495057;
}

.form-select,
.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 0.9rem;
}

.override-buttons {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: opacity 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-warning {
  background: #ffc107;
  color: #212529;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.current-override-info {
  margin-top: 16px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 4px;
}

.current-override-info p {
  margin: 4px 0;
  font-size: 0.85rem;
}


/* Settings Actions */
.settings-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.green-button {
  background: #28a745;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;
}

.green-button:hover:not(:disabled) {
  background: #218838;
}

.green-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.save-success {
  color: #28a745;
  font-weight: 500;
}

/* Next Season Block */
.next-season-block {
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}

.next-season-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.next-season-label {
  color: #6c757d;
  margin-right: 6px;
  font-size: 0.9rem;
}

.next-season-status {
  display: inline-block;
  margin-left: 10px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.next-season-status.configured {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.next-season-status.pending {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffe5a0;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 0.85rem;
}

.next-season-help {
  margin: 8px 0 0 0;
  font-size: 0.8rem;
  color: #6c757d;
}

/* Team Overrides Section */
.team-overrides-section {
  margin-bottom: 24px;
  padding: 16px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}

.team-overrides-section h4 {
  margin: 0 0 8px 0;
  font-size: 1rem;
}

.section-help-text {
  margin: 0 0 16px 0;
  color: #6c757d;
  font-size: 0.85rem;
}

.overrides-loading,
.overrides-empty {
  padding: 12px;
  font-size: 0.9rem;
  color: #6c757d;
}

.overrides-empty {
  background: #d4edda;
  color: #155724;
  border-radius: 4px;
}

.check-icon {
  font-weight: 700;
  margin-right: 6px;
}

.overrides-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.override-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: #fff8f0;
  border: 1px solid #ffe5d0;
  border-radius: 4px;
  flex-wrap: wrap;
}

.override-team-info {
  flex: 1;
  min-width: 140px;
  display: flex;
  flex-direction: column;
}

.override-team-name {
  font-weight: 600;
  font-size: 0.9rem;
}

.override-team-age {
  font-size: 0.75rem;
  color: #6c757d;
}

.override-team-dates {
  font-size: 0.85rem;
  color: #495057;
  flex: 1;
  min-width: 180px;
}

.btn-link {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 4px 8px;
}

.btn-link:hover:not(:disabled) {
  text-decoration: underline;
}

.btn-link:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.overrides-bulk {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

/* Responsive */
@media (max-width: 768px) {
  .settings-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .phase-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .phase-info {
    width: 100%;
  }

  .phase-dates {
    width: 100%;
  }

  .override-controls {
    flex-direction: column;
  }

  .form-group {
    width: 100%;
  }
}
</style>
